/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Complexe = require('../types/Complexe')
const kernel = require('../kernel/kernel')
const CCb = require('./CCb')
const CCbGlob = require('../kernel/CCbGlob')
module.exports = CSommeDansFormule

/**
 * Classe représentant une somme indicée dans un arbre binaire de calcul.
 * @constructor
 * @extends CCb
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CCb} calculASommer : Le calcul à sommer.
 * @param {CCb} indiceDebut : L'indice de départ.
 * @param {CCb} indiceFin : L'indice de fin.
 * @param {indiceFin} pas :  Le pas à ajouter à l'indice pour chaque somme (1 la plupart du temps)
 * @param {String} nomVariable : Le nom choisi pour la variable de sommation.
 * @returns {CSommeDansFormule}
 */
function CSommeDansFormule (listeProprietaire, calculASommer, indiceDebut, indiceFin,
  pas, nomVariable) {
  if (arguments.length === 1) CCb.call(this, listeProprietaire);
  else {
    CCb.call(this, listeProprietaire);
    this.calculASommer = calculASommer;
    this.indiceDebut = indiceDebut;
    this.indiceFin = indiceFin;
    this.pas = pas;
    this.nomVariable = nomVariable;
  }
}
CSommeDansFormule.prototype = new CCb();
CSommeDansFormule.prototype.constructor = CSommeDansFormule;
CSommeDansFormule.prototype.superClass = "CCb";
CSommeDansFormule.prototype.className = "CSommeDansFormule";


CSommeDansFormule.prototype.nature = function() {
  return CCbGlob.natSommeDansFormule;
};

CSommeDansFormule.prototype.dependDeVariable = function(ind) {
  return this.calculASommer.dependDeVariable(ind) || this.indiceDebut.dependDeVariable(ind)
    || this.indiceFin.dependDeVariable(ind) || this.pas.dependDeVariable(ind);
};

CSommeDansFormule.prototype.getClone = function(listeSource, listeCible) {
  return new CSommeDansFormule(listeCible, this.calculASommer.getClone(listeSource, listeCible),
    this.indiceDebut.getClone(listeSource, listeCible), this.indiceFin.getClone(listeSource, listeCible),
    this.pas.getClone(listeSource, listeCible), this.nomVariable);
};

CSommeDansFormule.prototype.getCopie = function() {
  return new CSommeDansFormule(this.listeProprietaire, this.calculASommer.getCopie(), this.indiceDebut.getCopie(),
      this.indiceFin.getCopie(), this.pas.getCopie(), this.nomVariable);
};

CSommeDansFormule.initialisePourDependance = function() {
  CCb.prototype.initialisePourDependance.call(this);
  this.calculASommer.initialisePourDependance();
  this.indiceDebut.initialisePourDependance();
  this.indiceFin.initialisePourDependance();
  this.pas.initialisePourDependance();
};

CSommeDansFormule.prototype.dependDe = function(p) {
    if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CCb.prototype.dependDe.call(this,p) ||
    this.calculASommer.dependDe(p) || this.indiceDebut.dependDe(p) || this.indiceFin.dependDe(p) || this.pas.dependDe(p));
};

CSommeDansFormule.prototype.dependDePourBoucle = function(p) {
  return this.calculASommer.dependDePourBoucle(p) || this.indiceDebut.dependDePourBoucle(p)
  || this.indiceFin.dependDePourBoucle(p) || this.pas.dependDePourBoucle(p);
};

CSommeDansFormule.prototype.existe = function(infoRandom) {
  return this.calculASommer.existe(infoRandom) && this.indiceDebut.existe(infoRandom)
    && this.indiceFin.existe(infoRandom) && this.pas.existe(infoRandom);
};

CSommeDansFormule.prototype.estConstant = function() {
  return this.calculASommer.estConstant() && this.indiceDebut.estConstant()
    && this.indiceFin.estConstant() && this.pas.estConstant();
};
/**
 * Renvoie le résultat réel de la somme.
 * @param {Boolean} infoRandom : true si les calculs aléatoires doivent être relancés
 * @returns {Float}
 */
CSommeDansFormule.prototype.resultat = function(infoRandom) {
  var val = new Array(1);
  var MIN_VALUE = -2147483648;
  var MAX_VALUE = 2147483647;
  var dinddeb = this.indiceDebut.resultat(infoRandom);
  if ((dinddeb > MAX_VALUE) || (dinddeb < MIN_VALUE) || (dinddeb !== Math.floor(dinddeb))) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var dindfin = this.indiceFin.resultat(infoRandom);
  if ((dindfin > MAX_VALUE) || (dindfin < MIN_VALUE) || (dindfin !== Math.floor(dindfin))
      || (dindfin < dinddeb)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var dpas = this.pas.resultat(infoRandom);
  if ((dpas > MAX_VALUE) || (dpas < MIN_VALUE)
      || (dpas !== Math.floor(dpas)) || (dpas <= 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  this.listeProprietaire.nombreIterations += (dindfin-dinddeb)/dpas;
  if (this.listeProprietaire.nombreIterations > CCbGlob.nombreMaxiIterations) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var somme = 0;
  for (var i = dinddeb; i <= dindfin; i+= dpas) {
    val[0] = i;
    somme += this.calculASommer.resultatFonction(infoRandom, val);
  }
  return somme;
};
/**
 * Renvoie le résultat complexe de la somme.
 * @param {Boolean} infoRandom : true si les calculs aléatoires doivent être relancés
 * @param {Complexe} zRes : Le complee contenant le résultatde la somme au retour.
 * @returns {undefined}
 */
CSommeDansFormule.prototype.resultatComplexe = function(infoRandom, zRes) {
  var MIN_VALUE = -2147483648;
  var MAX_VALUE = 2147483647;
  var val = new Array(1);
  val[0] = new Complexe();
  var inddeb = new Complexe();
  this.indiceDebut.resultatComplexe(infoRandom, inddeb);
  if (inddeb.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard());
  var dinddeb = inddeb.x;
  if ((dinddeb > MAX_VALUE) || (dinddeb < MIN_VALUE) || (dinddeb !== Math.floor(dinddeb))) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var indfin = new Complexe();
  this.indiceFin.resultatComplexe(infoRandom, indfin);
  if (inddeb.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var dindfin = indfin.x;
  if ((dindfin > MAX_VALUE) || (dindfin < MIN_VALUE) || (dindfin !== Math.floor(dindfin))
      || (dindfin < dinddeb)) throw Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var valpas = new Complexe();
  this.pas.resultatComplexe(infoRandom, valpas);
  if (valpas.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var dvalpas = valpas.x;
  if ((dvalpas > MAX_VALUE) || (dvalpas < MIN_VALUE)
      || (dvalpas !== Math.floor(dvalpas)) || (dvalpas <= 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  this.listeProprietaire.nombreIterations += (dindfin-dinddeb)/dvalpas;
  if (this.listeProprietaire.nombreIterations > CCbGlob.nombreMaxiIterations) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  zRes.x = 0;
  zRes.y = 0;
  var z = new Complexe(0, 0);
  for (var i = inddeb.x; i <= indfin.x; i+= valpas.x) {
    val[0].x = i;
    val[0].y = 0;
    this.calculASommer.resultatFonctionComplexe(infoRandom, val, z);
    zRes.x += z.x;
    zRes.y += z.y;
  }
};

CSommeDansFormule.prototype.resultatFonction = function(infoRandom, valeurParametre) {
  var val, i, dpas, dinddeb, dindfin, somme;
  var MIN_VALUE = -2147483648;
  var MAX_VALUE = 2147483647;
  if (valeurParametre instanceof Array) {
  	var n = valeurParametre.length;
  	val = new Array(n + 1);
  	for (i = 0; i < n; i++) val[i] = valeurParametre[i];
  }
  else {
    val = new Array(2);
    val[0] = valeurParametre;
  }
  dinddeb = this.indiceDebut.resultatFonction(infoRandom, valeurParametre);
  if ((dinddeb > MAX_VALUE) || (dinddeb < MIN_VALUE) || (dinddeb !== Math.floor(dinddeb))) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  dindfin = this.indiceFin.resultatFonction(infoRandom, valeurParametre);
  if ((dindfin > MAX_VALUE) || (dindfin < MIN_VALUE) || (dindfin !== Math.floor(dindfin))
      || (dindfin < dinddeb)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  dpas = this.pas.resultatFonction(infoRandom, valeurParametre);
  if ((dpas > MAX_VALUE) || (dpas < MIN_VALUE)
      || (dpas !== Math.floor(dpas)) || (dpas <= 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  this.listeProprietaire.nombreIterations += (dindfin-dinddeb)/dpas;
  if (this.listeProprietaire.nombreIterations > CCbGlob.nombreMaxiIterations) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  // int inddeb = (int) dinddeb;
  // int indfin = (int) dindfin;
  // int valpas = (int) dpas;
  somme = 0;
  for (i = dinddeb; i <= dindfin; i+= dpas) {
    val[1] = i;
    somme += this.calculASommer.resultatFonction(infoRandom, val);
  }
  return somme;
};

CSommeDansFormule.prototype.resultatFonctionComplexe = function(infoRandom, valeurParametre, zRes) {
  var i, val, dinddeb, dindfin, dvalpas, inddeb, indfin, z;
  var MIN_VALUE = -2147483648;
  var MAX_VALUE = 2147483647;
  if (valeurParametre instanceof Array) {
  	var n = valeurParametre.length;
  	val = new Array(n + 1);
  	for (i = 0; i < n; i++) val[i] = new Complexe(valeurParametre[i]);
  	val[n] = new Complexe();
  }
  else {
  	val = new Array(2);
  	val[0] = valeurParametre;
  	val[1] = new Complexe();

  }
  inddeb = new Complexe();
  this.indiceDebut.resultatFonctionComplexe(infoRandom, valeurParametre, inddeb);
  if (inddeb.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  dinddeb = inddeb.x;
  if ((dinddeb > MAX_VALUE) || (dinddeb < MIN_VALUE) || (dinddeb !== Math.floor(dinddeb))) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  indfin = new Complexe();
  this.indiceFin.resultatFonctionComplexe(infoRandom, valeurParametre, indfin);
  if (indfin.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  dindfin = indfin.x;
  if ((dindfin > MAX_VALUE) || (dindfin < MIN_VALUE) || (dindfin !== Math.floor(dindfin))
      || (dindfin < dinddeb)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  var valpas = new Complexe();
  this.pas.resultatFonctionComplexe(infoRandom, valeurParametre, valpas);
  if (valpas.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  dvalpas = valpas.x;
  if ((dvalpas > MAX_VALUE) || (dvalpas < MIN_VALUE)
      || (dvalpas !== Math.floor(dvalpas)) || (dvalpas <= 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  this.listeProprietaire.nombreIterations += (dindfin-dinddeb)/dvalpas;
  if (this.listeProprietaire.nombreIterations > CCbGlob.nombreMaxiIterations) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  zRes.x = 0;
  zRes.y = 0;
  z = new Complexe(0, 0);
  for (i = inddeb.x; i <= indfin.y; i+= valpas.x) {
    val[n].x = i;
    val[n].y = 0;
    this.calculASommer.resultatFonctionComplexe(infoRandom, val, z);
    zRes.x += z.x;
    zRes.y += z.y;
  }
};

CSommeDansFormule.prototype.chaineCalcul = function(varFor) {
  var varFor2, i;
  var n = 0;
  if (varFor === null) varFor2 = new Array(1);
  else {
    n = varFor.length;
    varFor2 = new Array(n+1);
  }
  for (i = 0; i < n; i++) varFor2[i] = varFor[i];
  varFor2[n] = this.nomVariable;
  return kernel.getString("somme") + "(" + this.calculASommer.chaineCalculSansPar(varFor2) + ","
    + this.nomVariable + "," + this.indiceDebut.chaineCalculSansPar(varFor) + ","
    + this.indiceFin.chaineCalculSansPar(varFor) + "," + this.pas.chaineCalculSansPar(varFor) + ")";
};

CSommeDansFormule.prototype.chaineLatex = function(varFor) {
  var varFor2,i,up;
  var n = 0;
  if (varFor === null) varFor2 = new Array(1);
  else {
    n = varFor.length;
    varFor2 = new Array(n+1);
  }
  for (i = 0; i < n; i++) varFor2[i] = varFor[i];
  varFor2[n] = this.nomVariable;
  up = "";
  if (this.pas.nature() === CCbGlob.natConstante) {
    if (this.pas.valeur === 1) up = this.indiceFin.chaineLatex(varFor);
  }
  if (up.length === 0) up = this.indiceFin.chaineLatex(varFor) + "\\text{ " + kernel.getString("step") + " }"+ this.pas.chaineLatex(varFor);
  return "\\sum\\limits_{"+this.nomVariable + "=" + this.indiceDebut.chaineLatexSansPar(varFor) + "}" + "^{"+ up+"}"+ "{" + this.calculASommer.chaineLatex(varFor2) + "}";
};
CSommeDansFormule.prototype.calculAvecValeursRemplacees = function() {
  return new CSommeDansFormule(this.listeProprietaire, this.calculASommer.calculAvecValeursRemplacees(),
    this.indiceDebut.calculAvecValeursRemplacees(), this.indiceFin.calculAvecValeursRemplacees(),
    this.pas.calculAvecValeursRemplacees(), this.nomVariable);
};
CSommeDansFormule.prototype.read = function(inps, list) {
  CCb.prototype.read.call(this, inps, list);
  this.nomVariable = inps.readUTF();
  this.calculASommer = inps.readObject(list);
  this.indiceDebut = inps.readObject(list);
  this.indiceFin = inps.readObject(list);
  this.pas = inps.readObject(list);
};

CSommeDansFormule.prototype.write = function(oups, list) {
  CCb.prototype.write.call(this,oups, list);
  oups.writeUTF(this.nomVariable);
  oups.writeObject(this.calculASommer);
  oups.writeObject(this.indiceDebut);
  oups.writeObject(this.indiceFin);
  oups.writeObject(this.pas);
};



// WEBPACK FOOTER //
// ./src/objets/CSommeDansFormule.js